import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchLegacy } from "@/lib/api";
import { Archive, Loader2, Trophy, GraduationCap, Activity, Stethoscope, Sparkles, BookOpen } from "lucide-react";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import useTrackPage from "@/hooks/useTrackPage";

const KIND_META = {
  honor_roll:        { Icon: Trophy,         label: "Honor Roll",      tint: "var(--fcc-noah)" },
  award:             { Icon: Trophy,         label: "Award",            tint: "var(--fcc-noah)" },
  championship:      { Icon: Trophy,         label: "Championship",     tint: "var(--fcc-noah)" },
  mvp:               { Icon: Trophy,         label: "MVP",              tint: "var(--fcc-noah)" },
  graduation:        { Icon: GraduationCap,  label: "Graduation",       tint: "var(--fcc-emma)" },
  report_card:       { Icon: GraduationCap,  label: "Report Card",      tint: "var(--fcc-emma)" },
  parent_conference: { Icon: BookOpen,       label: "Parent Conference",tint: "var(--fcc-emma)" },
  recital:           { Icon: Sparkles,       label: "Recital",          tint: "var(--fcc-peach)" },
  performance:       { Icon: Sparkles,       label: "Performance",      tint: "var(--fcc-peach)" },
  sports:            { Icon: Activity,       label: "Sports",           tint: "var(--fcc-olive)" },
  medical:           { Icon: Stethoscope,    label: "Medical",          tint: "var(--fcc-red-soft)" },
};

function fallbackMeta(kind) {
  return { Icon: Sparkles, label: kind || "Moment", tint: "var(--fcc-cream-2)" };
}

function LegacyInner() {
  useTrackPage("legacy_open");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChild, setActiveChild] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchLegacy()
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const children = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    data.years.forEach((y) => y.events.forEach((e) => {
      if (e.child_id && e.child_name && !seen.has(e.child_id)) {
        seen.set(e.child_id, e.child_name);
      }
    }));
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const filteredYears = useMemo(() => {
    if (!data) return [];
    if (!activeChild) return data.years;
    return data.years
      .map((y) => ({ ...y, events: y.events.filter((e) => e.child_id === activeChild) }))
      .filter((y) => y.events.length > 0);
  }, [data, activeChild]);

  return (
    <div data-testid="legacy-page" className="space-y-6">
      <header className="fcc-card p-6 sm:p-8">
        <p className="label-overline flex items-center gap-2"><Archive className="w-3 h-3" /> Family Legacy Timeline</p>
        <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-2">Every win, kept forever</h1>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-2xl">
          A cross-child timeline of your family&apos;s milestones across school, sports, activities, medical, and achievements. Grouped by year.
        </p>
        {children.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="legacy-child-filter">
            <button
              data-testid="legacy-filter-all"
              onClick={() => setActiveChild("")}
              className={`fcc-pill text-xs ${activeChild === "" ? "bg-[var(--fcc-ink)] text-white" : "bg-white text-[var(--fcc-ink)] border border-[var(--fcc-border)]"}`}
            >
              All children
            </button>
            {children.map((c) => (
              <button
                key={c.id}
                data-testid={`legacy-filter-${c.id}`}
                onClick={() => setActiveChild(c.id)}
                className={`fcc-pill text-xs ${activeChild === c.id ? "bg-[var(--fcc-ink)] text-white" : "bg-white text-[var(--fcc-ink)] border border-[var(--fcc-border)]"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </header>

      {loading ? (
        <div className="fcc-card p-12 text-center text-[var(--fcc-muted)]">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          <p className="text-sm mt-3">Building your family timeline…</p>
        </div>
      ) : !data || filteredYears.length === 0 ? (
        <div className="fcc-card p-12 text-center text-sm text-[var(--fcc-muted)]" data-testid="legacy-empty">
          <p className="font-semibold text-[var(--fcc-ink)] mb-2">No milestones yet.</p>
          <p>As KidHQ detects awards, report cards, and big moments in your emails, they&apos;ll appear here automatically.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {filteredYears.map((y) => (
            <YearBlock key={y.year} year={y} />
          ))}
        </div>
      )}
    </div>
  );
}

function YearBlock({ year }) {
  return (
    <section data-testid={`legacy-year-${year.year}`} className="fcc-card p-6 sm:p-7">
      <header className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-3xl text-[var(--fcc-ink)]">{year.year}</h2>
        <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{year.events.length} events</span>
      </header>
      <ol className="relative border-l-2 border-[var(--fcc-border)] pl-6 space-y-4">
        {year.events.map((e, idx) => {
          const meta = KIND_META[e.kind] || fallbackMeta(e.kind);
          const Icon = meta.Icon;
          return (
            <li key={idx} data-testid="legacy-event" className="relative">
              <span
                className="absolute -left-[34px] top-0.5 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center"
                style={{ backgroundColor: meta.tint }}
              >
                <Icon className="w-3 h-3 text-[var(--fcc-green)]" />
              </span>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <p className="font-semibold text-sm text-[var(--fcc-ink)]">{e.label}</p>
                <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">
                  {(e.at || "").slice(0, 10)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {e.child_name && (
                  <Link
                    to={e.child_id ? `/app/children/${e.child_id}/journey` : "/app/children"}
                    className="fcc-pill text-[10px] bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream)] transition"
                  >
                    {e.child_name}
                  </Link>
                )}
                {meta.label && (
                  <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{meta.label}</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default function LegacyPage() {
  return (
    <PageErrorBoundary scope="legacy">
      <LegacyInner />
    </PageErrorBoundary>
  );
}
